import React, { Component, Fragment } from 'react'
import track from 'react-tracking'
import PropTypes from 'prop-types'

import BEMHelper from '@ynap/bem-helper'
import { Button } from '@ynap/button'
import EmptyList from '@ynap/empty-list'
import Skeleton from '@ynap/skeleton'
import { WithReducedSiteFurniture } from '@ynap/with-reduced-site-furniture'

import { MyAccountNavWrapper } from '..'
import { myaccountPageData } from '../analytics'
import { MarketingPreferencesForm } from './MarketingPreferencesForm'

const bem = new BEMHelper('MarketingPreferences')

@track()
export class MarketingPreferencesPage extends Component {
    static propTypes = {
        messages: PropTypes.shape({
            marketing: PropTypes.shape({
                updateError: PropTypes.func,
            }),
        }).isRequired,
        loyaltyConsent: PropTypes.object,
        fetchContent: PropTypes.func.isRequired,
        fetchCountry: PropTypes.func.isRequired,
        fetchLoyaltyConsent: PropTypes.func,
        updateUserMarketingCountryLang: PropTypes.func.isRequired,
        isFetchingLoyaltyConsent: PropTypes.bool,
        fetchLoyaltyConsentError: PropTypes.object,
        userSummary: PropTypes.shape({
            hasMigratedToNewTierExpansion: PropTypes.bool,
            isEip: PropTypes.bool,
        }),
        preferredCountry: PropTypes.string,
        preferredLanguage: PropTypes.string,
        isStaff: PropTypes.bool,
        country: PropTypes.array,
        countries: PropTypes.object,
        content: PropTypes.object,
        location: PropTypes.string,
        locale: PropTypes.shape({
            id: PropTypes.string,
        }),
        isFetchingCountry: PropTypes.bool,
        isFetchingContent: PropTypes.bool,
        fetchCountryError: PropTypes.bool,
        fetchContentError: PropTypes.bool,
        getLinks: PropTypes.func.isRequired,
    }

    @track((props) =>
        myaccountPageData({
            name: props.analyticsName,
            subCategory1: props.analyticsName,
        })(props)
    )
    componentDidMount() {
        const {
            locale,
            history,
            country,
            content,
            fetchUser,
            fetchContent,
            fetchCountry,
            fetchCountries,
            isFetchingCountry,
            accessKey,
            email,
            validationCode,
        } = this.props

        if ((accessKey || validationCode) && email) {
            return history.push(
                `/${locale.id}/account/unsubscribenewsletter?email=${email}&accessKey=${encodeURIComponent(
                    accessKey || validationCode
                )}`
            )
        }

        fetchCountries()
        fetchUser()

        if (typeof content === 'undefined') {
            if (!country && !isFetchingCountry) {
                fetchCountry().then(() => {
                    if (typeof content?.response === 'undefined') {
                        fetchContent('subscribe-preferences-collection')
                    }
                })
            } else if (country || typeof content?.response === 'undefined') {
                if (typeof content?.response === 'undefined') {
                    fetchContent('subscribe-preferences-collection')
                }
            }
        }
    }

    render() {
        const {
            location,
            locale,
            countries,
            fetchCountry,
            isFetchingCountry,
            fetchCountryError,
            content,
            fetchContent,
            isFetchingContent,
            fetchContentError,
            updateUserMarketingCountryLang,
            updateUserMarketingCountryLangError,
            isUpdatingUserMarketingCountryLang,
            updateUserInfo,
            userSummary,
            userPreferences,
            isFetchingUser,
            fetchUserError,
            getLinks,
            nativeApp,
            messages,
            config,
            fetchUser,
        } = this.props

        const { fullPageHeader, enableGenderSelection } = config?.head?.flags || {}

        const accountNavProps = {
            userSummary,
            messages,
            location,
            getLinks,
            fullPageHeader,
            title: messages?.pages?.preferences?.title(),
        }
        const loading = isFetchingCountry || isFetchingContent
        const error = !!fetchCountryError || !!fetchContentError || !!fetchUserError

        const FormComponent = (
            <MarketingPreferencesForm
                locale={locale}
                content={content}
                countries={countries}
                userPreferences={userPreferences}
                isFetchingUser={isFetchingUser}
                updateUserMarketingCountryLang={updateUserMarketingCountryLang}
                isUpdatingUserMarketingCountryLang={isUpdatingUserMarketingCountryLang}
                updateUserMarketingCountryLangError={updateUserMarketingCountryLangError}
                updateUserInfo={updateUserInfo}
                messages={messages}
                nativeApp={nativeApp}
                enableGenderSelection={enableGenderSelection}
                fetchUser={fetchUser}
            />
        )

        const ErrorComponent = (
            <EmptyList
                className={bem('empty')}
                icon={<div className={bem('emptyStateIcon')} />}
                variant={'myAccountError'}
                title={messages.marketingPreferences.pageError()}
            >
                <Button
                    onClick={() => {
                        if (fetchCountryError) {
                            fetchCountry().then(() => {
                                fetchContent('subscribe-preferences-collection')
                            })
                        }

                        if (fetchContentError) {
                            fetchContent('subscribe-preferences-collection')
                        }
                    }}
                    type="primary-link"
                    className={bem('emptyLink')}
                >
                    {messages.marketingPreferences.retryNow()}
                </Button>
            </EmptyList>
        )

        const SkeletonComponent = (
            <Fragment>
                <div className={bem('titleSkeleton')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('skeletonTwo')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                    <Skeleton align={'left'} />
                </div>
                <div className={bem('defaultSkeleton')}>
                    <Skeleton count={3} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('defaultSkeleton')}>
                    <Skeleton count={2} align={'left'} className={bem('skeletonSpace')} />
                </div>
                <div className={bem('longParagraph')}>
                    <Skeleton count={2} className={bem('skeletonSpace')} />
                    <Skeleton align={'left'} />
                </div>
                <div className={bem('longParagraph', 'last')}>
                    <Skeleton count={2} className={bem('skeletonSpace')} />
                    <Skeleton align={'left'} />
                </div>
                <Skeleton count={1} className={bem('skeletonButton')} />
            </Fragment>
        )

        const MarketingPreferencesElements = error
            ? ErrorComponent
            : loading || !content || !userPreferences
            ? SkeletonComponent
            : FormComponent

        return nativeApp ? (
            <WithReducedSiteFurniture>{MarketingPreferencesElements}</WithReducedSiteFurniture>
        ) : (
            <MyAccountNavWrapper {...accountNavProps}>{MarketingPreferencesElements}</MyAccountNavWrapper>
        )
    }
}

export default MarketingPreferencesPage
